import { useMemo } from 'react';
import { Permission } from '../services/permissions.service';
import { userInfoAtom } from '../utils/atoms';
import { useAtomValue } from 'jotai';

/**
 * Hook to access user permissions based on their auth
 * Fetches auth information and provides permission checking utilities
 *
 * @returns Object with permissions and permission checking functions
 */
export const usePermissions = () => {
  const userInfo = useAtomValue(userInfoAtom);

  // Get current permissions from auth and convert string[] to Permission[]
  const permissions = useMemo(() => {
    if (!userInfo?.permissions || !Array.isArray(userInfo.permissions)) {
      return [];
    }

    // Convert string permissions to Permission enum values
    // Filter out any invalid permission values
    return userInfo.permissions;
  }, [userInfo?.permissions]);

  /**
   * Check if user has a specific permission
   * @param permission - Permission to check
   * @returns True if user has the permission
   */
  const can = useMemo(
    () => (permission: Permission) => {
      if (!permission) {
        return true;
      }
      return permissions.includes(permission);
    },
    [permissions],
  );

  /**
   * Check if user has all of the specified permissions
   * @param permissionList - Array of permissions to check
   * @returns True if user has at least one permission
   */
  const canAll = useMemo(
    () => (permissionList: Permission[]) => {
      if (!permissionList || permissionList.length === 0) {
        return true;
      }
      return permissionList.every((permission) =>
        permissions.includes(permission),
      );
    },
    [permissions],
  );

  return {
    permissions,
    can,
    canAll,
  };
};
